/**
 * Calendar Service for JonesCRM
 * Handles calendar events (meetings, calls, tasks) from SuiteCRM
 */

const CALENDAR_API_BASE = '/api/chat/calendar.php';

class CalendarService {
  /**
   * Get calendar events for a date range
   */
  async getEvents(startDate, endDate, userId = null, type = 'all') {
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        type: type
      });
      if (userId) {
        params.append('user_id', userId);
      }

      const response = await fetch(`${CALENDAR_API_BASE}?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch calendar events');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      throw error;
    }
  }

  /**
   * Create a new event
   */
  async createEvent(eventData) {
    try {
      const response = await fetch(CALENDAR_API_BASE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData),
      });
      if (!response.ok) {
        throw new Error('Failed to create event');
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  }

  /**
   * Update an existing event
   */
  async updateEvent(eventData) {
    try {
      const response = await fetch(CALENDAR_API_BASE, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData),
      });
      if (!response.ok) {
        throw new Error('Failed to update event');
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  /**
   * Delete an event
   */
  async deleteEvent(id, type) {
    try {
      const response = await fetch(CALENDAR_API_BASE, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id, type }),
      });
      if (!response.ok) {
        throw new Error('Failed to delete event');
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }

  /**
   * Get upcoming events for notifications
   */
  async getUpcomingEvents(userId, minutes = 15) {
    try {
      const now = new Date();
      const soon = new Date(now.getTime() + minutes * 60000);

      const params = new URLSearchParams({
        start_date: now.toISOString().split('T')[0],
        end_date: soon.toISOString().split('T')[0],
      });
      if (userId) {
        params.append('user_id', userId);
      }

      const response = await fetch(`${CALENDAR_API_BASE}?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch upcoming events');
      }

      const data = await response.json();

      // Filter events that are starting within the next X minutes
      const upcomingEvents = (data.events || []).filter(event => {
        const eventStart = new Date(event.start);
        return eventStart >= now && eventStart <= soon;
      });

      return upcomingEvents;
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      return [];
    }
  }
}

// Create singleton instance
const calendarService = new CalendarService();

export default calendarService;
